import {
  Exact,
  ParentUpcomingClassesQueryQuery,
  ParentUpcomingClassesQueryQueryVariables,
} from "@outschool/gql-frontend-generated";
import { calculateDateBounds, dayjs } from "@outschool/time";
import {
  ApolloQueryResult,
  gql,
  useQueryWithPreviousData,
} from "@outschool/ui-apollo";
import sortBy from "lodash/sortBy";
import React from "react";

import * as Activity from "../../shared/Activity";
import {
  ClassListInfoProps,
  EnrollmentType,
  SectionType,
} from "../components/loggedInHome/LoggedInHomeMyClassesList";
import { useTimeZone } from "../providers/TimeZoneProvider";

/**
 * How many *future* meetings we fetch for each section.
 * Keep in sync with actual limit in activeEnrollments.section.meetings.
 */
export const MAX_MEETINGS_TO_FETCH = 5;

const parentUpcomingClassesQuery = gql`
  query ParentUpcomingClassesQuery($start: DateTime!, $end: DateTime!) {
    currentUser {
      uid
      activeEnrollments(start: $start, end: $end) {
        uid
        canBeStopped
        canBePaused
        canBeWithdrawn
        isEnded
        isPaused
        ongoingStartTime
        ongoingEndTime
        refundableAmountCents
        confirmedAt
        withdrawn_at
        withdrawnAt
        price_cents
        priceCredits
        buyer {
          uid
          name
        }
        details {
          name
        }
        learner {
          uid
          name
          avatar
        }
        parent {
          uid
          name
        }
        payments {
          uid
          isOngoingRefundable
          paid_at
          unrefundedCents
          end_time
          service_fee_percentage
          credit_transaction_uid
          credit_account_type
        }
        paymentSchedule {
          uid
          stoppedAt
        }
        pauseWindow {
          uid
          startDate
          endDate
          canBeCanceled
          pauseReason
        }
        refundPolicy {
          name
          description
        }
        service_fee_percentage
        section {
          uid
          canceled_at
          deleted_at
          end_time
          price_cents
          priceCredits
          published_at
          start_time
          isBuyable
          meetingTimes {
            dayOfWeek
            hour
            minute
          }
          activity {
            uid
            duration_weeks
            duration_minutes
            size_max
            hasTeacherSchedule
            isClub
            isOneTime
            is_ongoing_weekly
            price_cents
            priceCredits
            published_at
            title
            titleTranslated
            weekly_meetings
            subject
            averageStarRating
            reviewCount
            age_min
            age_max
            hasTeacherSchedule
            isFlexSchedule
            isSelfPaced
            isIncludedWithMembership
            details {
              photo
            }
            leader {
              uid
              name
              photo
              leader_link
              timeZone
            }
          }
          leader {
            uid
            name
            photo
            leader_link
            timeZone
          }
          meetings(filter: Enrolled, startAfter: $start, limit: 5) {
            uid
            start_time
            end_time
          }
          nextOngoingMeeting {
            uid
            start_time
            end_time
          }
        }
      }
    }
  }
`;

function getNumRemainingMeetings(section: SectionType) {
  return section.meetings.filter(
    (m: $TSFixMe) => !m.deleted_at && dayjs(m.end_time).isAfter(),
  ).length;
}

export type UseParentUpcomingClassesQueryValues = {
  error?: Error | null;
  loading: boolean;
  refetch: (
    variables?: Partial<Exact<{ start: any; end: any }>> | undefined,
  ) => Promise<ApolloQueryResult<ParentUpcomingClassesQueryQuery>>;
  upcomingEnrollments: EnrollmentType[];
  renderedEnrollments: EnrollmentType[];
  totalCount: number;
};

const RETRY_COUNT = 3;
const BACKOFF_FACTOR = 1000; // 1 second

const useRetryUpcomingClassQuery = ({
  onCompleted,
  start,
  end,
  skip,
}: $TSFixMe) => {
  const [retryCount, setRetryCount] = React.useState(0);
  const [error, setError] = React.useState<Error | null>(null);
  const timerRef = React.useRef<NodeJS.Timeout | null>(null);

  const { loading, data, refetch } = useQueryWithPreviousData<
    ParentUpcomingClassesQueryQuery,
    ParentUpcomingClassesQueryQueryVariables
  >(parentUpcomingClassesQuery, {
    variables: { start, end },
    onCompleted: onCompleted,
    onError: err => {
      console.log("Error trying to fetch upcoming classes:", err);
      OsPlatform.captureError(err);
      setError(err);
    },
    skip: skip || retryCount > RETRY_COUNT,
  });

  React.useEffect(() => {
    if (error && retryCount <= RETRY_COUNT) {
      const backoff = Math.pow(BACKOFF_FACTOR, retryCount);
      timerRef.current = setTimeout(() => {
        setRetryCount(retryCount + 1);
        refetch();
      }, backoff);
    }
    return () => {
      if (!!timerRef?.current) {
        clearTimeout(timerRef?.current);
      }
    };
  }, [error, retryCount, refetch]);

  return { data, loading, error, refetch, retryCount };
};
export const useParentUpcomingClassesQuery = ({
  limitUpcomingClasses,
  filterByLearnerUid,
  onCompleted,
  includeNoMeetingsClasses,
  skip,
}: {
  limitUpcomingClasses?: number;
  filterByLearnerUid?: string | null;
  onCompleted?: (info: ClassListInfoProps) => void;
  includeNoMeetingsClasses?: boolean;
  skip?: boolean;
}): UseParentUpcomingClassesQueryValues => {
  const { timeZone } = useTimeZone();
  // "memoize" today's date so that on re-render the query doesn't erroneously refetch
  const todayRef = React.useRef(new Date());
  const { start, end } = calculateDateBounds(todayRef.current, null, timeZone);
  const { loading, data, error, refetch } = useRetryUpcomingClassQuery({
    onCompleted: () => {
      onCompleted?.({
        isLoading: loading,
        numClasses: renderedEnrollments?.length ?? 0,
      });
    },
    start,
    end,
    skip,
  });

  const sortedEnrollments = React.useMemo(() => {
    const { currentUser } = data || {};
    const { activeEnrollments = [] } = currentUser || {};

    return sortBy(activeEnrollments, enrollment => {
      // @ts-ignore TS(2339): Property 'section' does not exist on type '{ __typ... Remove this comment to see the full error message
      const { section } = enrollment;
      const { meetings, activity } = section;

      if (Activity.isFlexSchedule(activity)) {
        // Sort flex classes by their section start time
        return section.start_time;
      }

      const nextMeeting = meetings.length > 0 ? meetings[0] : null;
      // If there are no next meetings we will order to the end (like groups),
      // otherwise use next meeting start time
      return nextMeeting?.start_time;
    });
  }, [data]);

  let upcomingEnrollments = sortedEnrollments.filter(enrollment => {
    const hasTeacherSchedule = enrollment?.section?.activity.hasTeacherSchedule;
    const hasNextOngoingMeeting = !!enrollment?.section?.nextOngoingMeeting;
    const isUnpublished = !enrollment?.section?.activity?.published_at;
    const activeEnrollment = !enrollment?.isEnded;
    const isSelfPaced = enrollment?.section?.activity?.isSelfPaced;
    const hasRemainingMeetings =
      // flex should always be considered "in progress" when they have no meetings
      enrollment?.section?.activity?.isFlexSchedule ||
      getNumRemainingMeetings(enrollment?.section) > 0;

    // Since 1-on-1 format classes don't have sections that should show up
    // as upcoming, we need to only show them when they have an upcoming meeting
    const upcomingMeeting =
      (hasTeacherSchedule && hasNextOngoingMeeting) || hasNextOngoingMeeting;

    const unpublishedWithoutFutureMeeting =
      isUnpublished && !upcomingMeeting && !isSelfPaced;

    const shouldDisplayClass = includeNoMeetingsClasses
      ? activeEnrollment && !unpublishedWithoutFutureMeeting
      : activeEnrollment &&
        !unpublishedWithoutFutureMeeting &&
        hasRemainingMeetings;
    return shouldDisplayClass;
  });

  if (filterByLearnerUid) {
    upcomingEnrollments = upcomingEnrollments.filter(
      enrollment => enrollment?.learner?.uid === filterByLearnerUid,
    );
  }

  const limitedEnrollments =
    limitUpcomingClasses != null
      ? upcomingEnrollments.slice(0, limitUpcomingClasses)
      : null;
  const renderedEnrollments = limitedEnrollments ?? upcomingEnrollments;

  return {
    error,
    loading,
    refetch,
    upcomingEnrollments: upcomingEnrollments as EnrollmentType[],
    renderedEnrollments: renderedEnrollments as EnrollmentType[],
    totalCount: upcomingEnrollments.length,
  };
};
